import { Gradient, Rect } from "@revideo/2d";
import type { SignalValue } from "@revideo/core";

export interface Stop { offset: number; color: string }

/**
 * CSS `radial-gradient(circle at cx cy, …)` with its default farthest-corner size, as a canvas gradient over a box of `w`×`h`.
 * The gradient is in the box's own centre-origin coordinates.
 */
export function circleGradient(w: number, h: number, cx: number, cy: number, stops: Stop[]): Gradient {
  const x = (cx - 0.5) * w, y = (cy - 0.5) * h;
  const r = Math.max(Math.hypot(x + w / 2, y + h / 2), Math.hypot(x - w / 2, y + h / 2), Math.hypot(x + w / 2, y - h / 2), Math.hypot(x - w / 2, y - h / 2));
  return new Gradient({ type: "radial", from: [x, y], to: [x, y], fromRadius: 0, toRadius: r, stops });
}

/**
 * CSS `radial-gradient(ellipse at cx cy, …)` (farthest-corner) over a `w`×`h` box: a canvas radial gradient is a circle, so a square
 * is drawn with the circle and scaled to the ellipse's aspect. Returns the rect to place at the box's centre.
 */
export function ellipseRect(w: number, h: number, cx: number, cy: number, stops: Stop[], opacity: SignalValue<number> = 1): Rect {
  const x = (cx - 0.5) * w, y = (cy - 0.5) * h;
  // farthest-corner ellipse with the box's aspect: (dx/rx)² + (dy/ry)² = 1 with ry = rx·h/w at the farthest corner
  const dx = Math.max(Math.abs(x + w / 2), Math.abs(x - w / 2)), dy = Math.max(Math.abs(y + h / 2), Math.abs(y - h / 2));
  const k = h / w;
  const rx = Math.sqrt(dx * dx + (dy * dy) / (k * k)), ry = rx * k;
  const side = 2 * rx;
  const gradient = new Gradient({ type: "radial", from: [0, 0], to: [0, 0], fromRadius: 0, toRadius: rx, stops });
  // the square sits so that its centre lands on (x, y) after the y-scale
  return new Rect({ width: side, height: side, x, y: y, scale: [1, k], fill: gradient, opacity });
}

/** CSS `linear-gradient(to top | to bottom, …)` over a `w`×`h` box. */
export function verticalGradient(h: number, direction: "to top" | "to bottom", stops: Stop[]): Gradient {
  const from: [number, number] = direction === "to top" ? [0, h / 2] : [0, -h / 2];
  const to: [number, number] = direction === "to top" ? [0, -h / 2] : [0, h / 2];
  return new Gradient({ type: "linear", from, to, stops });
}
